import { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../components/admin/ForbiddenPanel";
import { UserListTable } from "../../components/admin/UserListTable";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import type { UserStatsRow } from "../../lib/admin/types";

type LoadState =
  | { status: "loading" }
  | { status: "ok"; users: UserStatsRow[] }
  | { status: "forbidden" }
  | { status: "error"; message: string };

const ACTIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Overview-lite: real, client-derived from the loaded /admin/users array only.
// "active" = had a session within the last 7 days. No fabricated metrics.
function summarize(users: UserStatsRow[]): { total: number; active: number } {
  const cutoff = Date.now() - ACTIVE_WINDOW_MS;
  const active = users.filter((u) => {
    if (!u.lastSessionAt) return false;
    const t = new Date(u.lastSessionAt).getTime();
    return !Number.isNaN(t) && t >= cutoff;
  }).length;
  return { total: users.length, active };
}

export function AdminUsersPage() {
  const t = useT();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    authedFetch("/admin/users")
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 403) {
          setState({ status: "forbidden" });
          return;
        }
        const body = await r.json();
        if (!body.ok) {
          setState({
            status: "error",
            message: body.error?.message ?? `Server returned ${r.status}.`,
          });
          return;
        }
        setState({ status: "ok", users: body.data.users });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : t("chrome.common.unknownError"),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout>
      {state.status === "loading" ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("admin.users.loading")}</p>
      ) : state.status === "forbidden" ? (
        <ForbiddenPanel />
      ) : state.status === "error" ? (
        <p
          role="alert"
          className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]"
        >
          {state.message}
        </p>
      ) : (
        <div className="space-y-6">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              {t("admin.users.title")}
            </h1>
            <p className="text-sm text-[var(--ink-muted)]">
              {t("admin.users.subFmt", summarize(state.users))}
            </p>
          </div>
          <UserListTable users={state.users} />
        </div>
      )}
    </AdminLayout>
  );
}
